import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { findWords } from './WordFinder';
import './App.css';

const BOARD_SIZE = 4;
const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const generateBoard = () => 
  Array.from({ length: BOARD_SIZE }, () => 
    Array.from({ length: BOARD_SIZE }, () => 
      LETTERS[Math.floor(Math.random() * LETTERS.length)]
    )
  );

const loadDictionary = async () => {
  try {
    const response = await fetch('words.txt');
    if(!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    
    const words = (await response.text())
      .split('\n')
      .map(word => word.trim().replace(/^"|"$/g, ''))
      .filter(word => word.length >= 3)
      .map(word => word.toUpperCase());

    console.log(`Loaded ${words.length} words from dictionary`);
    return words;
  } catch (error) {
    console.error('Failed to load dictionary:', error);
    return [];
  }
};

function WordHunt() {
  const [board, setBoard] = useState(generateBoard);
  const [dictionary, setDictionary] = useState([]);
  const [selectedWord, setSelectedWord] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const foundWords = useMemo(() => 
    dictionary.length ? findWords(board, dictionary) : [], 
    [board, dictionary]
  );

  const highlightedPath = useMemo(() => 
    foundWords.find(w => w.word === selectedWord)?.path || [], 
    [foundWords, selectedWord]
  );

  useEffect(() => {
    loadDictionary()
      .then(setDictionary)
      .finally(() => setIsLoading(false));
  }, []);

  const generateNewBoard = useCallback(() => {
    setBoard(generateBoard());
    setSelectedWord(null);
  }, []);

  const handleWordClick = useCallback((wordObj) => {
    setSelectedWord(prev => prev === wordObj.word ? null : wordObj.word);
  }, []);

  const isHighlighted = useCallback((row, col) => 
    highlightedPath.some(pos => pos.row === row && pos.col === col),
    [highlightedPath]
  );

  const getHighlightOrder = useCallback((row, col) => {
    const index = highlightedPath.findIndex(pos => pos.row === row && pos.col === col);
    return index !== -1 ? index + 1 : null;
  }, [highlightedPath]);

  if(isLoading) {
    return (
      <div className="app">
        <div className="container">
          <h1>Word Hunt Showcase</h1>
          <p>Loading dictionary...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="container">
        <header>
          <h1>Word Hunt Showcase</h1>
          <button onClick={generateNewBoard} className="generate-btn">
            Generate New Board
          </button>
        </header>

        <GameBoard 
          board={board}
          isHighlighted={isHighlighted}
          getHighlightOrder={getHighlightOrder}
        />

        {selectedWord && (
          <WordInfo selectedWord={selectedWord} />
        )}

        {foundWords.length > 0 && (
          <WordList 
            foundWords={foundWords}
            selectedWord={selectedWord}
            onWordClick={handleWordClick}
          />
        )}

        <Instructions />
      </div>
    </div>
  );
}

const GameBoard = React.memo(({ board, isHighlighted, getHighlightOrder }) => (
  <div className="board">
    {board.flat().map((letter, index) => {
      const row = Math.floor(index / BOARD_SIZE);
      const col = index % BOARD_SIZE;
      const highlighted = isHighlighted(row, col);
      const order = getHighlightOrder(row, col);

      return (
        <div 
          key={index}
          className={`cell ${highlighted ? 'highlighted' : ''}`}
        >
          {letter}
          {highlighted && <span className="order-badge">{order}</span>}
        </div>
      );
    })}
  </div>
));

const WordInfo = React.memo(({ selectedWord }) => (
  <div className="word-info">
    <p><strong>Showing path for: {selectedWord}</strong></p>
    <p>Follow the numbers to trace the word path</p>
  </div>
));

const WordList = React.memo(({ foundWords, selectedWord, onWordClick }) => (
  <div className="words-section">
    <h3>Words Found ({foundWords.length}):</h3>
    <div className="words-grid">
      {foundWords.map((wordObj, index) => (
        <button 
          key={index}
          onClick={() => onWordClick(wordObj)}
          className={`word-btn ${selectedWord === wordObj.word ? 'selected' : ''}`}
        >
          {wordObj.word}
        </button>
      ))}
    </div>
  </div>
));

const Instructions = React.memo(() => (
  <div className="instructions">
    <h3>How to Play:</h3>
    <ul>
      <li>Words can be formed by connecting adjacent letters (including diagonally)</li>
      <li>Each letter can only be used once per word</li>
      <li>Click on any word in the list to see how it's formed on the board</li>
      <li>Numbers show the order in which letters are connected</li>
    </ul>
  </div>
));

export default WordHunt;